import { Users, DollarSign, TrendingUp, UserPlus } from 'lucide-react'
import type { DashboardOutput } from '@/api/colaboradores-hooks'

interface HeadcountKpiCardsProps {
  data: DashboardOutput | undefined
  isLoading: boolean
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const formatCurrencyCompact = (value: number) => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1)}k`
  return formatCurrency(value)
}

export function HeadcountKpiCards({ data, isLoading }: HeadcountKpiCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const headcount = data?.headcount
  const payroll = data?.latestPayroll

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      <KpiCard
        title="Colaboradores Ativos"
        value={String(headcount?.active ?? '—')}
        subtitle={`${headcount?.total ?? 0} total`}
        icon={Users}
      />
      <KpiCard
        title="Custo Médio"
        value={payroll ? formatCurrencyCompact(payroll.avgCostPerEmployee) : '—'}
        subtitle="por colaborador/mês"
        icon={DollarSign}
      />
      <KpiCard
        title="Custo Total Mensal"
        value={payroll ? formatCurrencyCompact(payroll.totalCprov) : '—'}
        subtitle={payroll ? `Período: ${payroll.period}` : 'Sem dados'}
        icon={TrendingUp}
      />
      <KpiCard
        title="Turnover 90 dias"
        value={
          headcount
            ? `+${headcount.hiredLast90Days} / -${headcount.terminatedLast90Days}`
            : '—'
        }
        subtitle="admissões / desligamentos"
        icon={UserPlus}
      />
    </div>
  )
}

interface KpiCardProps {
  title: string
  value: string
  subtitle: string
  icon: React.ElementType
}

function KpiCard({ title, value, subtitle, icon: Icon }: KpiCardProps) {
  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-5 hover:border-brand-primary/40 transition-all duration-300 shadow-monumenta">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-monumenta bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-brand-primary" />
        </div>
        <div className="text-brand-gray text-xs font-body uppercase tracking-wider leading-tight">
          {title}
        </div>
      </div>
      <div className="text-white text-2xl font-display font-bold leading-none mb-1">
        {value}
      </div>
      <div className="text-brand-gray text-xs font-body">{subtitle}</div>
    </div>
  )
}

function KpiCardSkeleton() {
  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-monumenta bg-brand-dark-lighter" />
        <div className="h-3 w-24 bg-brand-dark-lighter rounded" />
      </div>
      <div className="h-7 w-20 bg-brand-dark-lighter rounded mb-1" />
      <div className="h-3 w-32 bg-brand-dark-lighter rounded" />
    </div>
  )
}
